import React, { useState, useEffect, useContext, useCallback } from "react";
import {
    Box,
    CssBaseline,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    Snackbar,
    Alert,
    Typography,
    Button,
    Divider,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
    Brightness4,
    Brightness7,
    Logout,
    Menu as MenuIcon,
} from "@mui/icons-material";
import { ThemeContext } from "../ThemeContext"; // Проверьте корректность пути
import { Link } from "react-router-dom";
import API, { getProfiles, reorderProfiles } from "../API";

// Импортируем компоненты из @dnd-kit
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy,
} from "@dnd-kit/sortable";

import ProfileCard from "./ProfileCard";
import debounce from "lodash.debounce";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL не задан в .env");
}

function Bot() {
    const { darkMode, toggleTheme } = useContext(ThemeContext);
    const [menuOpen, setMenuOpen] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "success",
    });
    const [activeId, setActiveId] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [isFetchingOnlineMen, setIsFetchingOnlineMen] = useState(false);

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const isMobile = useMediaQuery("(max-width: 1800px)");

    const theme = createTheme({
        palette: {
            mode: darkMode ? "dark" : "light",
            background: {
                default: darkMode ? "#0d1117" : "#f9f9f9",
            },
            text: {
                primary: darkMode ? "#c9d1d9" : "#000000",
                secondary: darkMode ? "#8b949e" : "#5f6368",
            },
        },
    });

    const fetchProfilesData = useCallback(async () => {
        try {
            const response = await getProfiles();
            const profilesWithAvatars = await Promise.all(
                response.data.map(async (profile) => {
                    let avatarUrl = `${API_BASE_URL}/static/default-avatar.jpg`;
                    try {
                        const avatarHash = profile.avatar_hash;
                        if (avatarHash) {
                            const checkResponse = await API.get(`/profiles/avatars/${avatarHash}`);
                            if (checkResponse.data.exists) {
                                avatarUrl = `${API_BASE_URL}/static/photos/${avatarHash}.jpg`;
                            }
                        }
                    } catch (error) {
                        console.error(
                            `Ошибка при получении аватара для профиля ${profile.id}:`,
                            error
                        );
                    }
                    return { ...profile, avatar_url: avatarUrl };
                })
            );
            setProfiles(profilesWithAvatars);
        } catch (error) {
            console.error(
                "Ошибка при получении профилей:",
                error.response?.data || error
            );
            setSnackbar({
                open: true,
                message: "Не удалось загрузить профили.",
                type: "error",
            });
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            window.location.href = "/";
        } else {
            fetchProfilesData();
        }
    }, [fetchProfilesData]);

    const handleDeleteProfile = async (id) => {
        try {
            await API.deleteProfile(id);
            setProfiles(profiles.filter((profile) => profile.id !== id));
            setSnackbar({
                open: true,
                message: "Профиль успешно удален!",
                type: "success",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Не удалось удалить профиль. Попробуйте снова.",
                type: "error",
            });
            console.error("Ошибка при удалении профиля:", error);
        }
    };

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    const longPressTimeout = 300;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: longPressTimeout,
                tolerance: 5,
            },
        })
    );

    const saveOrder = useCallback(
        debounce(async (orderedIds) => {
            try {
                const numericOrderedIds = orderedIds.map((id) => parseInt(id, 10));
                console.log("Сохранение порядка профилей:", numericOrderedIds);
                const response = await reorderProfiles(numericOrderedIds);
                console.log("Ответ сервера при сохранении порядка:", response.data);
                setSnackbar({
                    open: true,
                    message: "Порядок профилей сохранен!",
                    type: "success",
                });
                await fetchProfilesData();
            } catch (error) {
                console.error("Ошибка при сохранении порядка профилей:", error);
                if (error.response) {
                    console.error("Данные ответа сервера:", error.response.data);
                    console.error("Статус:", error.response.status);
                    console.error("Заголовки:", error.response.headers);
                    const errorMessage =
                        error.response.data.message ||
                        "Не удалось сохранить порядок профилей.";
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        type: "error",
                    });
                } else if (error.request) {
                    console.error("Ответ не получен:", error.request);
                    setSnackbar({
                        open: true,
                        message: "Не удалось связаться с сервером.",
                        type: "error",
                    });
                } else {
                    console.error("Ошибка при настройке запроса:", error.message);
                    setSnackbar({
                        open: true,
                        message: "Произошла ошибка при отправке запроса.",
                        type: "error",
                    });
                }
            }
        }, 500),
        [fetchProfilesData]
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        setEditMode(true);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setProfiles((prevProfiles) => {
                const oldIndex = prevProfiles.findIndex(
                    (profile) => profile.id.toString() === active.id
                );
                const newIndex = prevProfiles.findIndex(
                    (profile) => profile.id.toString() === over.id
                );
                const newProfiles = arrayMove(prevProfiles, oldIndex, newIndex);
                const orderedIds = newProfiles.map((profile) => profile.id.toString());
                saveOrder(orderedIds);
                return newProfiles;
            });
        }
        setActiveId(null);
        setEditMode(false);
    };

    const handleDragCancel = () => {
        setActiveId(null);
        setEditMode(false);
    };

    const handleProfileClick = (profileId) => {
        setSelectedProfileId(profileId);
        console.log(`Профиль ${profileId} выбран`);
    };

    const handleLetterChange = (profileId, newText) => {
        setProfiles((prevProfiles) =>
            prevProfiles.map((profile) =>
                profile.id === profileId
                    ? { ...profile, letter: newText }
                    : profile
            )
        );
    };

    const toggleFetchingOnlineMen = async () => {
        if (isFetchingOnlineMen) {
            try {
                await API.stopFetchMenOnline(selectedProfileId);
                setSnackbar({
                    open: true,
                    message: "Получение мужчин онлайн остановлено.",
                    type: "success",
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: "Не удалось остановить процесс получения мужчин онлайн.",
                    type: "error",
                });
            }
        } else {
            try {
                await API.fetchMenOnline(selectedProfileId);
                setSnackbar({
                    open: true,
                    message: "Получение мужчин онлайн запущено.",
                    type: "success",
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: "Не удалось запустить процесс получения мужчин онлайн.",
                    type: "error",
                });
            }
        }
        setIsFetchingOnlineMen(!isFetchingOnlineMen);
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.type}
                    sx={{
                        width: "100%",
                        backgroundColor:
                            snackbar.type === "success" ? "#238636" : "#d32f2f",
                        color: "#fff",
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Box
                sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                }}
            >
                <IconButton onClick={toggleTheme} color="inherit">
                    {darkMode ? <Brightness7 /> : <Brightness4 />}
                </IconButton>
            </Box>

            {isMobile && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        zIndex: 1000,
                    }}
                >
                    <IconButton onClick={toggleMenu} color="inherit">
                        <MenuIcon />
                    </IconButton>
                </Box>
            )}

            {!isMobile && (
                <Drawer
                    variant="permanent"
                    anchor="left"
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: "240px",
                            backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                            color: theme.palette.text.primary,
                        },
                    }}
                >
                    <List>
                        <ListItem button component={Link} to="/Home_menu">
                            <ListItemText primary="Домой" />
                        </ListItem>
                        <ListItem button component={Link} to="/add-profile">
                            <ListItemText primary="Добавить профайл" />
                        </ListItem>
                        <ListItem button component={Link} to="/Bot">
                            <ListItemText primary="Бот" />
                        </ListItem>
                        <ListItem button onClick={handleLogout}>
                            <Logout />
                            <ListItemText primary="Выход" />
                        </ListItem>
                        <Divider />
                        <Box sx={{ padding: "16px" }}>
                            <Typography variant="h6" gutterBottom>
                                Мужчины онлайн
                            </Typography>
                            <Button
                                variant="contained"
                                color={isFetchingOnlineMen ? "error" : "success"}
                                onClick={toggleFetchingOnlineMen}
                                fullWidth
                            >
                                {isFetchingOnlineMen
                                    ? "Остановить получение"
                                    : "Запустить получение"}
                            </Button>
                        </Box>
                    </List>
                </Drawer>
            )}

            <Drawer
                anchor="left"
                open={menuOpen}
                onClose={toggleMenu}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: "240px",
                        backgroundColor: darkMode ? "#0d1117" : "#f9f9f9",
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <List>
                    <ListItem button component={Link} to="/Home_menu">
                        <ListItemText primary="Домой" />
                    </ListItem>
                    <ListItem button component={Link} to="/add-profile">
                        <ListItemText primary="Добавить профайл" />
                    </ListItem>
                    <ListItem button component={Link} to="/Bot">
                        <ListItemText primary="Бот" />
                    </ListItem>
                    <ListItem button onClick={handleLogout}>
                        <Logout />
                        <ListItemText primary="Выход" />
                    </ListItem>
                    <Divider />
                    <Box sx={{ padding: "16px" }}>
                        <Typography variant="h6" gutterBottom>
                            Мужчины онлайн
                        </Typography>
                        <Button
                            variant="contained"
                            color={isFetchingOnlineMen ? "error" : "success"}
                            onClick={toggleFetchingOnlineMen}
                            fullWidth
                        >
                            {isFetchingOnlineMen
                                ? "Остановить получение"
                                : "Запустить получение"}
                        </Button>
                    </Box>
                </List>
            </Drawer>

            <Box
                sx={{
                    width: isMobile ? "90vw" : "70vw",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    borderRadius: "8px",
                    margin: "0 auto",
                    padding: "2rem",
                    minHeight: "100vh",
                    background: darkMode
                        ? "radial-gradient(circle, #0d1117 0%, #161b22 100%)"
                        : "radial-gradient(circle, #f9f9f9 0%, #ffffff 100%)",
                    boxShadow: darkMode
                        ? "0 4px 20px rgba(0, 0, 0, 0.8)"
                        : "0 4px 20px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                }}
            >
                {editMode && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            zIndex: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            animation: "fadeIn 0.3s",
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                color: darkMode ? "#c9d1d9" : "#000000",
                                fontWeight: "bold",
                            }}
                        >
                            Режим редактирования
                        </Typography>
                    </Box>
                )}

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <SortableContext
                        items={profiles.map((profile) => profile.id.toString())}
                        strategy={rectSortingStrategy}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: "1rem",
                                width: "100%",
                            }}
                        >
                            {profiles.map((profile) => (
                                <ProfileCard
                                    key={profile.id}
                                    id={profile.id.toString()}
                                    profile={profile}
                                    onClick={handleProfileClick}
                                    onLetterChange={handleLetterChange}
                                    editMode={editMode && activeId === profile.id.toString()}
                                    onUpdate={fetchProfilesData}
                                />
                            ))}
                        </Box>
                    </SortableContext>

                    <DragOverlay>
                        {activeId ? (
                            <ProfileCard
                                id={activeId}
                                profile={
                                    profiles.find((p) => p.id.toString() === activeId) || {}
                                }
                                onClick={() => {}}
                                onLetterChange={() => {}}
                                editMode={true}
                                onUpdate={() => {}}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </Box>
        </ThemeProvider>
    );
}

export default Bot;
